import React, { createContext, ReactNode, useState, useEffect } from 'react'
import { getSavedSearchesForUser, searchVehicles, saveExistingSavedSearch, deleteSavedSearch } from 'apis/vehicleApis'
import { SavedSearchGroup } from 'types/savedSearchTypes'
import { ISearchVehiclesRequest, DropdownData, ISearchFilterResult } from 'types/vehicleTypes'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { getSearchUrl } from 'utils/urlUtils'
import { NotificationFormData } from './NotificationModal'
import { useHistory } from 'react-router-dom'

const EDIT_SEARCH_NAME = 'Edit SearchName'
const EDIT_NOTIFICATIONS = 'Edit Notifications'
export interface SavedSearchContextType {
  savedSearchesData: SavedSearchGroup[] | null
  loading: boolean
  error: string | null
  selectedSearch: SavedSearchGroup | null
  searchFrequencies: DropdownData[] | null
  searchDays: DropdownData[] | null
  searchDurations: DropdownData[] | null
  loadSavedSearches: () => void
  saving: boolean
  handleEditSearchName: (search: SavedSearchGroup) => void
  handleEditNotification: (search: SavedSearchGroup) => void
  handleDeleteSearch: (search: SavedSearchGroup) => void
  handleSaveSearchName: (editName: string) => void
  handleSaveNotifications: (formData: NotificationFormData) => void
  handleDeleteConfirm: () => void
  runSearch: (search: SavedSearchGroup) => void
  closeAllModals: () => void
}
interface SavedSearchProviderProps {
  children: ReactNode
}

export const SavedSearchContext = createContext<SavedSearchContextType | undefined>(undefined)

export const SavedSearchProvider: React.FC<SavedSearchProviderProps> = ({ children }) => {
  const [savedSearchesData, setSavedSearchesData] = useState<SavedSearchGroup[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedSearch, setSelectedSearch] = useState<SavedSearchGroup | null>(null)
  const [searchFrequencies, setSearchFrequencies] = useState<DropdownData[] | null>(null)
  const [searchDurations, setSearchDurations] = useState<DropdownData[] | null>(null)
  const [searchDays, setSearchDays] = useState<DropdownData[] | null>(null)
  const [saving, setSaving] = useState(false)
  const { userInteraction } = useDtmAnalytics()
  const history = useHistory()

  const updateSearchInState = (searchId: number, updates: Partial<SavedSearchGroup>) => {
    setSavedSearchesData((prev) => {
      if (!prev) return prev
      return prev.map((search) => (search.ID === searchId ? { ...search, ...updates } : search))
    })
  }

  const loadSavedSearches = async () => {
    setError(null)
    setLoading(true)
    try {
      const response = await getSavedSearchesForUser()
      const searches = response.searches

      setSearchDurations(response.durations)
      setSearchFrequencies(response.frequency)
      setSearchDays(response.days)

      setSavedSearchesData(
        searches.map((search) => ({
          ...search,
          vehicleCount: 0,
          searchIsRunning: true
        }))
      )
      userInteraction('Saved Search')
      setLoading(false)

      const searchPromises = searches.map(async (search) => {
        try {
          const searchRequest: ISearchVehiclesRequest = {
            PageNumber: 0,
            PageSize: 1,
            SortBy: { Name: 'Model ASC', Value: 'Model ASC', SortOrder: 1 },
            SearchQuery: {
              SearchFilters: search?.Filters,
              ReturnOnlyVehicles: false,
              SortBy: [],
              StatusIds: []
            },
            ReturnZeroCountFilters: false,
            ReturnVehicles: false,
            QuickSearch: false,
            Ids: []
          }
          const searchResults = await searchVehicles(searchRequest)
          return {
            id: search.ID,
            vehicleCount: searchResults?.TotalRecords || 0
          }
        } catch (error) {
          console.error(`Failed to get count for search ID: ${search.ID}`, error)
          return { id: search.ID, vehicleCount: 0, error: true }
        }
      })

      const results = await Promise.all(searchPromises)

      setSavedSearchesData((currentSearches) =>
        currentSearches.map((search) => {
          const result = results.find((r) => r.id === search.ID)
          return {
            ...search,
            vehicleCount: result ? result.vehicleCount : 0,
            searchIsRunning: false
          }
        })
      )
    } catch (err) {
      setError('Failed to load saved searches. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleEditSearchName = (search: SavedSearchGroup) => {
    setSelectedSearch(search)
  }

  const handleEditNotification = (search: SavedSearchGroup) => {
    setSelectedSearch(search)
  }

  const handleDeleteSearch = (search: SavedSearchGroup) => {
    setSelectedSearch(search)
  }

  const handleSaveSearchName = async (editName: string) => {
    if (!selectedSearch) return
    setSaving(true)
    try {
      if (editName.trim() === '') {
        editName = 'Unnamed Search'
      }
      const updatedSearchData = {
        ...selectedSearch,
        Description: editName
      }

      await saveExistingSavedSearch(updatedSearchData)
      userInteraction(`MySavedSearch: ${selectedSearch.Description} : ${EDIT_SEARCH_NAME}`)
      updateSearchInState(selectedSearch.ID, { Description: editName })
    } catch (error) {
      updateSearchInState(selectedSearch.ID, { Description: editName })
    } finally {
      setSaving(false)
      setSelectedSearch(null)
    }
  }

  const handleSaveNotifications = async (formData: NotificationFormData) => {
    if (!selectedSearch) return
    setSaving(true)
    const notificationUpdates = {
      NotifyMe: formData.notifyMe,
      EmailRecurrance: formData.emailRecurrence,
      EmailDay: formData.emailDay,
      EmailRecurranceDuration: formData.emailRecurrenceDuration,
      EmailFormat: formData.emailFormat
    }

    try {
      const updatedSearchData = {
        ...selectedSearch,
        ...notificationUpdates
      }

      await saveExistingSavedSearch(updatedSearchData)
      userInteraction(`MySavedSearch: ${selectedSearch.Description} : ${EDIT_NOTIFICATIONS}`)
      updateSearchInState(selectedSearch.ID, notificationUpdates)
    } catch (error) {
      updateSearchInState(selectedSearch.ID, notificationUpdates)
    } finally {
      setSaving(false)
      setSelectedSearch(null)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!selectedSearch) return
    try {
      setSaving(true)
      await deleteSavedSearch(selectedSearch)
      setSavedSearchesData((prev) => {
        if (!prev) return prev
        return prev.filter((search) => search.ID !== selectedSearch.ID)
      })
      setSelectedSearch(null)
    } catch (error) {
    } finally {
      setSaving(false)
    }
  }

  const closeAllModals = () => {
    setSelectedSearch(null)
  }

  const runSearch = (search: SavedSearchGroup) => {
    try {
      const sessionFilterData = sessionStorage.getItem('SearchFilters')
      let sessionFilter = sessionFilterData ? (JSON.parse(sessionFilterData) as ISearchFilterResult[]) : null

      if (sessionFilter) {
        sessionFilter.forEach((filter) => {
          filter.Selected = false
          filter.Value = ''
          filter.ChildrenFilter.forEach((child) => {
            child.Selected = false
          })
        })

        search.Filters.forEach((searchFilter) => {
          const existingFilterIndex = sessionFilter.findIndex(
            (filter) => filter.DBFieldName === searchFilter.DBFieldName
          )

          if (existingFilterIndex > -1) {
            sessionFilter[existingFilterIndex].ChildrenFilter.push(searchFilter)
          } else {
            sessionFilter.push(searchFilter)
          }
        })

        sessionStorage.setItem('SearchFilters', JSON.stringify(sessionFilter))
      } else {
        sessionStorage.setItem('SearchFilters', JSON.stringify(search.Filters))
      }

      sessionStorage.setItem('QueryFilters', '')
      sessionStorage.setItem('SearchID', search.ID.toString())
      sessionStorage.setItem('SearchDescription', search.Description)
      sessionStorage.setItem('sourceSearch', 'SavedSearch')
      sessionStorage.setItem('customClickName', `MySavedSearch: ${search.Description}: ${search.action}`)

      history.push(getSearchUrl())
    } catch (error) {}
  }

  useEffect(() => {
    loadSavedSearches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue: SavedSearchContextType = {
    savedSearchesData,
    loading,
    error,
    selectedSearch,
    searchFrequencies,
    searchDays,
    searchDurations,
    loadSavedSearches,
    saving,
    handleEditSearchName,
    handleEditNotification,
    handleDeleteSearch,
    handleSaveSearchName,
    handleSaveNotifications,
    handleDeleteConfirm,
    runSearch,
    closeAllModals
  }

  return <SavedSearchContext.Provider value={contextValue}>{children}</SavedSearchContext.Provider>
}
